import { type ScenarioRun } from "@/app/scenarios/columns"

// ─── Форматирование времени ────────────────────────────────────────────────────

/**
 * Форматирует секунды в читаемую строку с часами, минутами и секундами.
 * Примеры: "5 с", "3 мин 20 с", "1 ч 5 мин", "2 ч 30 мин 15 с"
 */
export function fmtSeconds(totalSec: number): string {
  const sec = Math.max(0, Math.round(totalSec))
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const s = sec % 60
  if (h > 0) {
    if (m === 0 && s === 0) return `${h} ч`
    if (s === 0) return `${h} ч ${m} мин`
    return `${h} ч ${m} мин ${s} с`
  }
  if (m === 0) return `${s} с`
  if (s === 0) return `${m} мин`
  return `${m} мин ${s} с`
}

/**
 * Форматирует миллисекунды в читаемую строку.
 */
export function fmtMs(ms: number): string {
  return fmtSeconds(ms / 1000)
}

/**
 * Форматирует минуты (дробные) в читаемую строку.
 */
export function fmtMinutes(minutes: number): string {
  return fmtSeconds(minutes * 60)
}

// ─── Расчёт длительности из dataJson ──────────────────────────────────────────

/**
 * Вычисляет суммарное время прохождения в миллисекундах как сумму длительностей
 * всех сессий шагов в хронологии (timeline).
 *
 * Приоритет: сумма шагов timeline (новый формат) → null (если данных нет).
 * Вызывающий код должен сам сделать fallback на totalTime/endTime-startTime.
 */
export function calcTotalTimeFromDataJson(dataJson: string | null | undefined): number | null {
  if (!dataJson || typeof dataJson !== 'string') return null
  try {
    const parsed = JSON.parse(dataJson)
    if (parsed.newFormat === true && Array.isArray(parsed.timeline) && parsed.timeline.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const totalMs = (parsed.timeline as any[]).reduce((sum: number, s: any) => {
        const d = Math.max(0, (s.endTime || 0) - (s.startTime || 0))
        return sum + d
      }, 0)
      if (totalMs > 0) return totalMs
    }
  } catch {}
  return null
}

/**
 * Вычисляет суммарную длительность в минутах, используя три источника данных:
 * 1. Сумма шагов из dataJson.timeline (наиболее точно, исключает паузы)
 * 2. Поле totalTime из ответа сервера
 * 3. Разница endTime − startTime (fallback)
 */
export function calcViewMinutes(opts: {
  dataJson?: string | null
  totalTime?: number | null
  endTime?: number | null
  startTime?: number | null
}): number {
  // 1. Сумма шагов из timeline
  const fromTimeline = calcTotalTimeFromDataJson(opts.dataJson)
  if (fromTimeline !== null) return fromTimeline / 60000

  // 2. totalTime с сервера (в ms)
  if (opts.totalTime && opts.totalTime > 0) return opts.totalTime / 60000

  // 3. Fallback: end - start
  return Math.max(0, ((opts.endTime || 0) - (opts.startTime || 0)) / 60000)
}

/**
 * Вычисляет суммарное время в мс для набора timeline-сессий
 * (только завершённых — у которых есть endTime).
 */
export function calcTimelineTotalMs(
  sessions: Array<{ startTime: number; endTime?: number; isActive?: boolean }>
): number {
  return sessions.reduce((sum, s) => {
    if (s.isActive || !s.endTime) return sum
    return sum + Math.max(0, s.endTime - s.startTime)
  }, 0)
}

// ─── Прочее ───────────────────────────────────────────────────────────────────

/**
 * Определяет платформу отчёта из dataJson.
 * Логика с обратной совместимостью:
 * 1. Если поле `platform` присутствует — возвращает его.
 * 2. Если `newFormat: true` — это десктоп (новый формат = веб-клиент).
 * 3. Если `newFormat: false` или отсутствует — это мобильное приложение (старый формат).
 */
export function getPlatformFromDataJson(dataJson: string | null | undefined): 'desktop' | 'mobile' | null {
  if (!dataJson || typeof dataJson !== 'string') return null
  try {
    const parsed = JSON.parse(dataJson)
    // Приоритет: явное поле platform
    if (parsed.platform === 'desktop' || parsed.platform === 'mobile') return parsed.platform
    // Fallback по формату: newFormat=true → десктоп, иначе → мобильное (старый формат)
    return parsed.newFormat === true ? 'desktop' : 'mobile'
  } catch {
    return null
  }
}

/**
 * Тепловая окраска ячейки процента:
 * 100%    → green-500
 * 0–99%  → red-500 → yellow-400
 */
export function heatmapStyle(percent: number): { backgroundColor: string; color: string } {
  if (percent >= 100) {
    return { backgroundColor: "var(--color-green-500)", color: "white" }
  }
  const p = Math.round((Math.min(percent, 99) / 99) * 100)
  return {
    backgroundColor: `color-mix(in oklch, var(--color-red-500), var(--color-yellow-400) ${p}%)`,
    color: "white",
  }
}

/**
 * Возвращает true, если отчёт абсолютно пустой:
 * 0 выполненных шагов И ни одного заполненного поля / фото / ответа.
 *
 * Такие отчёты нужно скрывать из всех таблиц и списков.
 */
/**
 * Возвращает timestamp (мс) последнего записанного действия в хронологии отчёта.
 * Используется для «виртуальной паузы»: если с этого момента прошло > 30 минут,
 * отчёт считается приостановленным пользователем.
 * Поддерживает только новый формат (newFormat: true) с timeline.
 */
export function getLastActivityTimestampMs(dataJson: string | null | undefined): number | null {
  if (!dataJson || typeof dataJson !== 'string') return null
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const parsed: any = JSON.parse(dataJson)
    if (parsed.newFormat === true && Array.isArray(parsed.timeline) && parsed.timeline.length > 0) {
      const last = parsed.timeline[parsed.timeline.length - 1]
      const t = last.endTime ?? last.startTime
      if (t != null && Number(t) > 0) return Number(t)
    }
  } catch {}
  return null
}

/** Порог отсутствия активности для виртуальной паузы (30 минут в мс). */
export const VIRTUAL_PAUSE_THRESHOLD_MS = 30 * 60 * 1000

/**
 * Возвращает true если отчёт формально не отправлен и последнее действие
 * было более VIRTUAL_PAUSE_THRESHOLD_MS назад.
 */
export function isVirtuallyPaused(
  lastActivityTimestampMs: number | null | undefined,
  isSubmitted: boolean | undefined,
): boolean {
  if (isSubmitted) return false
  if (lastActivityTimestampMs == null) return false
  return Date.now() - lastActivityTimestampMs > VIRTUAL_PAUSE_THRESHOLD_MS
}

/** Тело отчёта помечено как отброшенное: черновик отчёта-файла, который нельзя удалить (см. discardFileReport) */
export function isReportDataDiscarded(data: unknown): boolean {
  if (typeof data === "string") {
    if (!data.includes('"discarded"')) return false
    try {
      data = JSON.parse(data)
    } catch {
      return false
    }
  }
  return !!data && typeof data === "object" && (data as { discarded?: unknown }).discarded === true
}

export function isEmptyReport(row: ScenarioRun): boolean {
  if (isReportDataDiscarded(row.dataJson)) return true

  // Есть хотя бы один выполненный шаг → не пустой
  if (row.completedSteps !== undefined && row.completedSteps > 0) return false

  // Нет dataJson → полностью пустой
  if (!row.dataJson || typeof row.dataJson !== "string") return true

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const parsed: any = JSON.parse(row.dataJson)

    // Новый формат (newFormat: true) — данные в timeline
    if (parsed.newFormat === true) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const timeline: any[] = Array.isArray(parsed.timeline) ? parsed.timeline : []
      return !timeline.some(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (item: any) => item.formReport || item.answer || item.photoId,
      )
    }

    // Старый формат — данные в steps
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const steps: any[] = Array.isArray(parsed.steps) ? parsed.steps : []
    return !steps.some(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (step: any) => step.formReport || step.answer || step.photoId,
    )
  } catch {
    return true
  }
}
